import React, { useEffect, useMemo } from "react";

import { DropDownButtonTypes } from "devextreme-react/drop-down-button";
import Tabs from "devextreme-react/tabs";
import Toolbar, { Item } from "devextreme-react/toolbar";
import { useTranslation } from "react-i18next";

// import cogLoop from '@iconify/icons-line-md/cog-loop';
import {
  Case,
  Switch,
  useAllotmentRef,
  useComponentStack,
  useWindowDimensions,
} from "@app/contexts";
import eventEmitter from "@app/contexts/emitter";
import { EComponentStack, SPV_RIGHT_TABS } from "@app/state/constants";
import { useFxDispatch, useFxSelector } from "@app/statefx";
import { schema } from "@app/statefx/schema";
import { selectFindPdfOfFXMID } from "@app/statefx/selectors/uiSpvMain.sel";
import { workerReboot, workerRebootClean } from "@app/statefx/xThunks/ds";
import { fxUiUpdateSpvGrid } from "@app/statefx/xThunks/uiSpvMain";
import "@app/style/dx.generic.catetory-chooser-compact.min.css";
import arrowSmallRight from "@iconify/icons-line-md/arrow-small-right";
// import clipboardIcon from '@iconify/icons-line-md/clipboard';
// import clipboardToClipboardCheckTransition from '@iconify/icons-line-md/clipboard-to-clipboard-check-transition';
import cloudOutlineLoop from "@iconify/icons-line-md/cloud-outline-loop";
import computerIcon from "@iconify/icons-line-md/computer";
import downloadingLoop from "@iconify/icons-line-md/downloading-loop";
import homeIcon from "@iconify/icons-line-md/home";
import { Icon } from "@iconify/react";

import "./invoices.scss";

import type { TRepoSPVMessages } from "@app/types/insider";
const baseColor = "#7c9fd5"; // '#3F51B5';
const clDone = `${baseColor}80`;
const clSelected = baseColor;
const clNotDone = "#c0c0c080";
const sizeCurrent = 20;
const sizeNotCurrent = 20;
const CLOUD = "CLOUD";
const HOME = "HOME";
const COMPUTER = "COMPUTER";
type TCrtPos = typeof CLOUD | typeof HOME | typeof COMPUTER;
const responseTimeout = 12_000;

export const SPVToolbarTop = ({
  role = "Header",
}: {
  role: "Header" | "VerticalTabsCollapsed";
}) => {
  const fxDispatch = useFxDispatch();
  const { components: stack } = useComponentStack(EComponentStack.nop);
  const { clickfn, currentCollapsedState } = useAllotmentRef();
  const [showProcess, setShowProcess] = React.useState<boolean>(false);
  const [panelCollapsed, setPanelCollapsed] = React.useState<boolean>(
    currentCollapsedState
  );
  const { appIsPersonalKarma } = useWindowDimensions();

  const _crtMessage = useFxSelector(schema.uiSpvMain.select);
  const crtMessage = _crtMessage.currentRecord as TRepoSPVMessages;
  const hardCopy = useFxSelector((s) =>
    selectFindPdfOfFXMID(s, { FXMID: crtMessage?.FXM_ID })
  );
  const isInvoiceTracking = useMemo(
    () => stack.includes(EComponentStack.InvoiceTracking),
    [stack]
  );

  const isInvoiceOverall = useMemo(
    () => stack.includes(EComponentStack.InvoiceOverall),
    [stack]
  );

  const isSpvMessages = useMemo(
    () => stack.includes(EComponentStack.SpvMessages),
    [stack]
  );

  const crtPosition = useMemo(() => {
    if (!crtMessage?.FSH_ID) return "CLOUD";
    if (crtMessage?.FSH_ID && Boolean(hardCopy)) return "COMPUTER";
    return "HOME";
  }, [crtMessage, hardCopy]) as TCrtPos;
  const { selectedRightTab } = useFxSelector(schema.uiSpvGrid.select);

  const cloudColor =
    crtPosition === CLOUD
      ? clSelected
      : [HOME, COMPUTER].includes(crtPosition)
      ? clDone
      : clNotDone;
  const sizeCloud = crtPosition === CLOUD ? sizeCurrent : sizeNotCurrent;
  const homeColor =
    crtPosition === HOME
      ? clSelected
      : crtPosition === "CLOUD"
      ? clNotDone
      : clDone;
  const sizeHome = crtPosition === HOME ? sizeCurrent : sizeNotCurrent;
  const computerColor = crtPosition === COMPUTER ? clSelected : clNotDone;
  const sizeComputer = crtPosition === COMPUTER ? sizeCurrent : sizeNotCurrent;

  const dataSource = [
    {
      text: "Factura SPV",
      key: SPV_RIGHT_TABS.FILE,
      // disabled: !JUST_IN_BROWSER,
      visible: isSpvMessages,
    },
    {
      text: "Partener",
      key: SPV_RIGHT_TABS.CONTRACTOR,
      visible: isSpvMessages,
    }, //TODO: https://github.com/VldMrgnn/jk-react-app/issues/76 implement the contractor info
    {
      text: "Operatii",
      key: SPV_RIGHT_TABS.OPERATIONS,
      visible: false,
    }, // todo: https://github.com/VldMrgnn/jk-react-app/issues/77 implement the operations
    {
      text: "Incasari",
      key: SPV_RIGHT_TABS.PAYMENTS,
      visible: (isInvoiceTracking || isInvoiceOverall) && !isSpvMessages,
    },
  ];
  useEffect(() => {
    let hideLoaderTimeout;

    const handleReadPdfFile = (data) => {
      if (data === "start") {
        setShowProcess(true);
        // Set timeout to hide loader if no response
        hideLoaderTimeout = setTimeout(() => {
          setShowProcess(false);
        }, responseTimeout);
      } else if (data === "done") {
        clearTimeout(hideLoaderTimeout); // Clear the timeout set for hiding the loader
        setShowProcess(false);
      }
    };

    eventEmitter.on("/xthnk/readPdfFile", handleReadPdfFile);

    // Cleanup listener and timeout when the component is unmounted
    return () => {
      eventEmitter.off("/xthnk/readPdfFile", handleReadPdfFile);
      clearTimeout(hideLoaderTimeout);
    };
  }, []);

  const tabsDispatch = (e) => {
    fxDispatch(
      fxUiUpdateSpvGrid({
        key: "selectedRightTab",
        value: e.itemData.key,
      })
    );
  };
  const onItemClick = (e: DropDownButtonTypes.ItemClickEvent) => {
    // notify(e.itemData.name || e.itemData, 'success', 600);
    if (e.itemData === "Reincarca nou") {
      fxDispatch(workerReboot());
    } else if (e.itemData === "Refacere cache") {
      fxDispatch(workerRebootClean());
    }
  };
  const collapsedItemClick = (e) => {
    setPanelCollapsed((_) => clickfn(null));
    fxDispatch(
      fxUiUpdateSpvGrid({
        key: "selectedRightTab",
        value: e.itemData.key,
      })
    );
  };
  useEffect(() => {
    setPanelCollapsed(currentCollapsedState);
  }, [currentCollapsedState]);

  // mainsot: EUIIMainSOT;
  useEffect(() => {
    const key = isSpvMessages ? SPV_RIGHT_TABS.FILE : SPV_RIGHT_TABS.PAYMENTS;
    tabsDispatch({ itemData: { key } });
    // if (appIsPersonalKarma) {
    // }
  }, [isSpvMessages, appIsPersonalKarma]);

  return (
    <div>
      <Toolbar
        id="spvToolbarTop"
        height={31}
        className="dx-swatch-catetory-chooser-compact"
      >
        <Item
          location="before"
          widget="dxButton"
          cssClass="toggleRightPaneButton"
          options={{
            icon: panelCollapsed ? "hidepanel" : "showpanel",
            stylingMode: "text",
            type: "default",
            onClick: (e) => setPanelCollapsed((_) => clickfn(e)),
          }}
          visible={role === "Header"}
        />
        <Item
          // three tabs.: Files (default), Contractor, Operations
          location="before"
          widget="dxTabs"
          cssClass="spvrighttabs"
          options={{
            selectedItemKeys: [selectedRightTab],
            keyExpr: "key",
            dataSource: dataSource,
            // selectedIndex: -1,
            onItemClick: tabsDispatch,
          }}
          visible={!panelCollapsed}
        />
        <Item
          location="after"
          render={() => {
            return (
              <Icon
                icon={downloadingLoop}
                color={"#da5359"}
                width={sizeCurrent}
                height={sizeCurrent}
              />
            );
          }}
          visible={showProcess}
        />
        <Item
          location="after"
          render={() => {
            return (
              <Icon
                icon={cloudOutlineLoop}
                color={cloudColor}
                width={sizeCloud}
                height={sizeCloud}
              />
            );
          }}
          visible={!panelCollapsed}
        />
        <Item
          location="after"
          render={() => {
            return (
              <Icon
                icon={arrowSmallRight}
                color={clNotDone}
                width={sizeNotCurrent}
                height={sizeNotCurrent}
              />
            );
          }}
          visible={!panelCollapsed}
        />
        <Item
          location="after"
          render={() => {
            return (
              <Icon
                icon={homeIcon}
                color={homeColor}
                width={sizeHome}
                height={sizeHome}
              />
            );
          }}
          visible={!panelCollapsed}
        />
        <Item
          location="after"
          render={() => {
            return (
              <Icon
                icon={arrowSmallRight}
                color={clNotDone}
                width={sizeNotCurrent}
                height={sizeNotCurrent}
              />
            );
          }}
          visible={!panelCollapsed}
        />
        <Item
          location="after"
          render={() => {
            return (
              <Icon
                icon={computerIcon}
                color={computerColor}
                width={sizeComputer}
                height={sizeComputer}
              />
            );
          }}
          visible={!panelCollapsed}
        />
        <Item
          location="after"
          widget="dxButton"
          locateInMenu="always"
          options={{
            icon: "clearformat",
            text: "Refresh Cache",
            stylingMode: "text",
            type: "default",
            onClick: () => {
              fxDispatch(workerReboot());
            },
          }}
          visible={false}
        />
        <Item
          location="after"
          widget="dxButton"
          locateInMenu="always"
          options={{
            icon: "clear",
            text: "Stergere Cache",
            stylingMode: "text",
            type: "danger",
            onClick: () => {
              fxDispatch(workerRebootClean());
            },
          }}
          visible={false}
        />
      </Toolbar>
      <Switch value={panelCollapsed && role === "VerticalTabsCollapsed"}>
        <Case when={true}>
          <Tabs
            id="withIconAndText"
            width={110}
            keyExpr={"key"}
            rtlEnabled={false}
            dataSource={dataSource}
            scrollByContent={false}
            showNavButtons={false}
            orientation={"vertical"}
            stylingMode={"primary"}
            iconPosition={"start"}
            selectedItemKeys={[selectedRightTab]}
            onItemClick={collapsedItemClick}
          />
        </Case>
        <Case when={false}>
          <div></div>
        </Case>
      </Switch>
    </div>
  );
};

export const SPVToolbar = () => {
  const { t } = useTranslation();
  const _crtMessage = useFxSelector(schema.uiSpvMain.select);
  const crtMessage = _crtMessage.currentRecord as TRepoSPVMessages;
  return (
    <div>
      <Toolbar style={{ margin: "auto", height: "60px" }}>
        <Item
          cssClass="noIco"
          widget="dxButton"
          location="center"
          options={{
            icon: "attach",
            text: t("DownloadSpvMessage"),
            width: 100,
            height: 58,
          }}
          // visible={!Boolean(crtMessage.FSH_ID)}
          visible={false}
        />
        <Item
          cssClass="noIco"
          widget="dxButton"
          location="center"
          options={{
            icon: "folder",
            text: t("UnArchiveSpvMessage"),
            width: 100,
            height: 58,
          }}
          // visible={Boolean(crtMessage.FSH_ID)}
          visible={false}
        />

        <Item
          cssClass="noIco"
          widget="dxButton"
          location="before"
          options={{
            icon: "user",
            text:
              crtMessage.QRESULT_TYPE === "FACTURA PRIMITA"
                ? t("Vendor")
                : t("Client"),
            width: 100,
            height: 58,
            stylingMode: "text",
          }}
          visible={true}
        />
        <Item
          cssClass="noIco"
          widget="dxButton"
          location="center"
          options={{
            icon: "share",
            text: t("share"),
            width: 100,
            height: 58,
          }}
          visible={false}
        />
        <Item
          cssClass="noIco"
          widget="dxButton"
          location="center"
          options={{
            icon: "clear",
            text: t("cleanup"),
            width: 100,
            height: 58,
          }}
          visible={false}
        />
      </Toolbar>
    </div>
  );
};

export const SPVFolderSelectToolbar = () => {
  return (
    <Toolbar height={31} className="dx-swatch-catetory-chooser-compact">
      <Item
        location="before"
        widget="dxButton"
        cssClass="silvertext"
        options={{
          icon: "home",
          stylingMode: "text",
          text: "C:\\Program Files (x86)\\SWS\\Jurnal_Gold\\Rapoarte\\",
          width: "100%",
          height: 36,
        }}
        visible={true}
      />
    </Toolbar>
  );
};
